/**
 * Transactions data layer (FR-TXN-01 to FR-TXN-21).
 *
 * Every mutation is a single `writeBatch` carrying the transaction document **and** the
 * account balance changes it implies (AD-02). Batched writes queue offline and
 * `increment()` is a commutative server-side transform, so an entry made on a plane
 * shows the right balance immediately and merges correctly on reconnect.
 *
 * `runTransaction()` must never appear in this file: it needs a read-then-write round
 * trip and so does not work offline, which would defeat FR-PLT-04.
 *
 * Monthly rollups join the same batch at M4. The shape is already right for them.
 */

import {
  collection,
  doc,
  increment,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  where,
  writeBatch,
  type QueryConstraint,
} from 'firebase/firestore';
import {
  balanceEffects,
  buildTransaction,
  collectDeltas,
  reverseEffects,
  validateTransaction,
  type AccountDelta,
  type Transaction,
  type TransactionInput,
  type TransactionType,
} from '../core/transactions';
import { addDays, todayKey, type DateKey, type MonthKey } from '../core/dates';
import { CUTOVER } from '../core/seed';
import { serverFilter, type Filters } from '../core/filters';
import { customRange, periodRange } from '../core/period';
import { editDeltas, rollupDeltas, type AccountTypeLookup } from '../core/rollups';
import { auditDelete, auditUpdate } from './audit';
import { stageRollupDeltas } from './rollups';
import { db } from './firebase';
import { releaseSource, reportSnapshotMetadata, trackWrite } from './syncState';

const COLLECTION = 'transactions';
const TRASH_DAYS = 30;

export class TransactionError extends Error {}

export function toTransaction(id: string, d: Record<string, unknown>): Transaction {
  return {
    id,
    type: (d.type as TransactionType) ?? 'expense',
    dateKey: (d.dateKey as DateKey) ?? todayKey(),
    monthKey: (d.monthKey as MonthKey) ?? '',
    fyKey: (d.fyKey as string) ?? '',
    accountId: (d.accountId as string) ?? '',
    toAccountId: (d.toAccountId as string) ?? null,
    amountPaise: (d.amountPaise as number) ?? 0,
    amountExpression: (d.amountExpression as string) ?? null,
    currency: (d.currency as 'INR' | 'USD') ?? 'INR',
    ratePpm: (d.ratePpm as number) ?? 1_000_000,
    rateIsStale: d.rateIsStale === true,
    amountInrPaise: (d.amountInrPaise as number) ?? (d.amountPaise as number) ?? 0,
    toAmountPaise: (d.toAmountPaise as number) ?? null,
    categoryId: (d.categoryId as string) ?? null,
    subcategoryId: (d.subcategoryId as string) ?? null,
    description: (d.description as string) ?? null,
    payee: (d.payee as string) ?? null,
    referenceNo: (d.referenceNo as string) ?? null,
    notes: (d.notes as string) ?? null,
    tagIds: (d.tagIds as string[]) ?? [],
    isBusiness: d.isBusiness === true,
    searchTokens: (d.searchTokens as string[]) ?? [],
    attachments: (d.attachments as Transaction['attachments']) ?? [],
    receivableId: (d.receivableId as string) ?? null,
    loanId: (d.loanId as string) ?? null,
    instalmentNo: (d.instalmentNo as number) ?? null,
    principalPaise: (d.principalPaise as number) ?? null,
    interestPaise: (d.interestPaise as number) ?? null,
    migrated: d.migrated === true,
    importBatchId: (d.importBatchId as string) ?? null,
    needsEnrichment: d.needsEnrichment === true,
    quickCaptured: d.quickCaptured === true,
    deleted: d.deleted === true,
    enteredBy: (d.enteredBy as string) ?? '',
  };
}

/** Map raw Firestore docs to transactions. Shared with the rollup recompute. */
export function toTransactionList(
  docs: { id: string; data: () => Record<string, unknown> }[],
): Transaction[] {
  return docs.map((d) => toTransaction(d.id, d.data()));
}

// ---------------------------------------------------------------------------
// Reading
// ---------------------------------------------------------------------------

export interface TransactionQuery {
  /** Required. Every query is anchored on a bounded period (SD-08). */
  monthKey?: MonthKey;
  fyKey?: string;
  /**
   * An arbitrary from–to range, for a custom reporting period (FR-RPT-01).
   *
   * A range on `dateKey` is a bounded anchor in exactly the same sense a `monthKey`
   * equality is, and it reuses the same composite indexes — `(deleted, accountId,
   * dateKey)` serves a range on `dateKey` just as it serves an ordering by it.
   */
  range?: { start: DateKey; end: DateKey };
  /** At most ONE of these goes server-side; the rest filter client-side. */
  accountId?: string;
  categoryId?: string;
  type?: TransactionType;
}

/**
 * The query a set of filters implies.
 *
 * Built in one place so the transaction list, bulk reclassify and reports cannot drift
 * apart in what they ask Firestore for — the same reason there is only one filter engine
 * (WD-06). Screens pass the result straight to `watchTransactions`.
 */
export function queryForFilters(filters: Filters): TransactionQuery {
  const server = serverFilter(filters);
  const { period } = filters;
  const custom = customRange(period);

  return {
    ...(custom
      ? { range: custom }
      : period.scope === 'month'
        ? { monthKey: period.key }
        : period.basis === 'fy'
          ? { fyKey: period.key }
          // A calendar year has no stored key to match, so it is asked for as the range
          // it is. `fyKey` would be the wrong anchor and `monthKey` cannot span twelve.
          : { range: periodRange(period) }),
    ...(server?.field === 'accountId' ? { accountId: server.value } : {}),
    ...(server?.field === 'categoryId' ? { categoryId: server.value } : {}),
    ...(server?.field === 'type' ? { type: server.value as TransactionType } : {}),
  };
}

/** A stable string for the query, for use as a React effect dependency. */
export function queryKey(spec: TransactionQuery): string {
  return JSON.stringify([
    spec.range?.start ?? null, spec.range?.end ?? null,
    spec.monthKey ?? null, spec.fyKey ?? null,
    spec.accountId ?? null, spec.categoryId ?? null, spec.type ?? null,
  ]);
}

/**
 * Watch a bounded period.
 *
 * SD-08: the period is the anchor and exactly one further filter goes to Firestore,
 * because a composite index is needed per query *shape* and the cap is 200. The
 * candidate set is small by construction — a month is ~167 documents, a year ~2,000 —
 * so refining the rest in memory is cheap.
 */
export function watchTransactions(
  spec: TransactionQuery,
  onChange: (transactions: Transaction[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const constraints: QueryConstraint[] = [where('deleted', '==', false)];

  if (spec.range) {
    constraints.push(
      where('dateKey', '>=', spec.range.start),
      where('dateKey', '<=', spec.range.end),
    );
  } else if (spec.monthKey) constraints.push(where('monthKey', '==', spec.monthKey));
  else if (spec.fyKey) constraints.push(where('fyKey', '==', spec.fyKey));

  if (spec.accountId) constraints.push(where('accountId', '==', spec.accountId));
  else if (spec.categoryId) constraints.push(where('categoryId', '==', spec.categoryId));
  else if (spec.type) constraints.push(where('type', '==', spec.type));

  constraints.push(orderBy('dateKey', 'desc'));

  // `includeMetadataChanges` is required, not optional: without it the callback does
  // not fire when only the pending-write flag changes, and the sync indicator never
  // updates after an offline entry.
  const unsubscribe = onSnapshot(
    query(collection(db, COLLECTION), ...constraints),
    { includeMetadataChanges: true },
    (snapshot) => {
      reportSnapshotMetadata('transactions', snapshot.metadata);
      onChange(snapshot.docs.map((d) => toTransaction(d.id, d.data())));
    },
    // Surfaced rather than swallowed: an empty list and a permission failure look
    // identical to the user otherwise.
    (error) => onError?.(error),
  );

  return () => {
    releaseSource('transactions');
    unsubscribe();
  };
}

// ---------------------------------------------------------------------------
// Writing
// ---------------------------------------------------------------------------

/**
 * Balance effects as the accounts see them.
 *
 * Anchored on the cutover, so a transaction dated before 1 Aug 2026 counts against its
 * account but moves no balance (FR-ACC-06a). Every write path goes through this — an
 * ungated `balanceEffects` call anywhere in the data layer would silently roll the
 * archive forward again.
 */
function onBalance(txn: Transaction): ReturnType<typeof balanceEffects> {
  return balanceEffects(txn, { cutover: CUTOVER });
}

function applyDeltas(
  batch: ReturnType<typeof writeBatch>,
  deltas: Map<string, AccountDelta>,
): void {
  for (const [accountId, delta] of deltas) {
    if (delta.balancePaise === 0 && delta.transactionCount === 0) continue;
    batch.update(doc(db, 'accounts', accountId), {
      balancePaise: increment(delta.balancePaise),
      transactionCount: increment(delta.transactionCount),
      updatedAt: serverTimestamp(),
    });
  }
}

export async function createTransaction(
  input: TransactionInput,
  by: string,
  accountType: AccountTypeLookup,
): Promise<string> {
  const issues = validateTransaction(input);
  if (issues.length > 0) throw new TransactionError(issues[0].message);

  const ref = doc(collection(db, COLLECTION));
  const txn = buildTransaction(ref.id, input, by);

  const batch = writeBatch(db);
  batch.set(ref, {
    ...stripId(txn),
    deletedAt: null,
    purgeAfter: null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  applyDeltas(batch, collectDeltas(onBalance(txn), []));
  stageRollupDeltas(batch, txn.monthKey, rollupDeltas(txn, accountType, 1));

  await trackWrite(batch.commit());
  return ref.id;
}

/**
 * Edit a transaction.
 *
 * Reverses the old balance effects and applies the new ones in the same batch, so a
 * change of amount, account or type can never leave a balance half-updated. When the
 * account changes, `transactionCount` moves with it — which matters because that count
 * is what the rules use to decide whether an account may be deleted.
 */
export async function updateTransaction(
  existing: Transaction,
  input: TransactionInput,
  by: string,
  accountType: AccountTypeLookup,
): Promise<void> {
  const issues = validateTransaction(input);
  if (issues.length > 0) throw new TransactionError(issues[0].message);

  const next = buildTransaction(existing.id, input, existing.enteredBy);

  const batch = writeBatch(db);
  batch.update(doc(db, COLLECTION, existing.id), {
    ...stripId(next),
    updatedAt: serverTimestamp(),
  });
  applyDeltas(
    batch,
    collectDeltas(onBalance(next), reverseEffects(onBalance(existing))),
  );
  // An edit can move a transaction across months, so this may touch two rollups.
  for (const [monthKey, fields] of editDeltas(existing, next, accountType)) {
    stageRollupDeltas(batch, monthKey, fields);
  }

  await trackWrite(batch.commit());
  await auditUpdate(
    COLLECTION,
    existing.id,
    existing as unknown as Record<string, unknown>,
    next as unknown as Record<string, unknown>,
    by,
  );
}

/**
 * Soft delete into the 30-day trash (FR-TXN-18, FR-DEL-01).
 *
 * Hard delete is denied to every client in the rules; only the purge job removes a
 * document. Balance effects are reversed now rather than at purge, so the figures are
 * right the moment the row disappears from the list.
 */
export async function softDeleteTransaction(
  txn: Transaction,
  by: string,
  accountType: AccountTypeLookup,
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, COLLECTION, txn.id), {
    deleted: true,
    deletedAt: serverTimestamp(),
    purgeAfter: addDays(todayKey(), TRASH_DAYS),
    updatedAt: serverTimestamp(),
  });
  applyDeltas(batch, collectDeltas([], reverseEffects(onBalance(txn))));
  stageRollupDeltas(batch, txn.monthKey, rollupDeltas(txn, accountType, -1));

  await trackWrite(batch.commit());
  await auditDelete(COLLECTION, txn.id, txn as unknown as Record<string, unknown>, by);
}

/** Restore from trash, re-applying the balance effects (FR-DEL-04). */
export async function restoreTransaction(
  txn: Transaction,
  by: string,
  accountType: AccountTypeLookup,
): Promise<void> {
  const batch = writeBatch(db);
  batch.update(doc(db, COLLECTION, txn.id), {
    deleted: false,
    deletedAt: null,
    purgeAfter: null,
    updatedAt: serverTimestamp(),
  });
  applyDeltas(batch, collectDeltas(onBalance(txn), []));
  stageRollupDeltas(batch, txn.monthKey, rollupDeltas(txn, accountType, 1));

  await trackWrite(batch.commit());
  await auditUpdate(COLLECTION, txn.id, { deleted: true }, { deleted: false }, by);
}

function stripId(txn: Transaction): Omit<Transaction, 'id'> {
  const { id: _id, ...rest } = txn;
  return rest;
}
